import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ModalShell from './ModalShell';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { createManualPunch } from '../../../../redux/actions/SocietyPortal/AttendanceAction/AttendanceAction';
import { getEmployeesDropdown } from '../../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { fromLocalInputValue, nowLocalInputValue } from './datetime';

// "Add missed punch" — POST /api/attendance/manual (attendance.manage).
// For fixing a WRONG punch use the edit modal instead; this records a punch
// the employee never made (forgot to tap out, dead phone, …).
const ManualPunchModal = ({ isOpen, onClose, onDone }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { manualPunchLoading } = useSelector(state => state.AttendanceReducer || {});
    const { employeesDropdownData, employeesDropdownLoading } = useSelector(state => state.EmployeeReducer || {});

    const [employeeId, setEmployeeId] = useState('');
    const [punchType, setPunchType] = useState('IN');
    const [punchAt, setPunchAt] = useState('');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setEmployeeId('');
        setPunchType('IN');
        setPunchAt(nowLocalInputValue());
        setReason('');
        setNotes('');
        if (societyId) dispatch(getEmployeesDropdown({ societyId }));
    }, [isOpen, dispatch, societyId]);

    // /api/employees/dropdown rows: { employee_id, first_name, last_name, email, employee_type }
    const employeeOptions = (Array.isArray(employeesDropdownData) ? employeesDropdownData : []).map(e => {
        const name = [e.first_name, e.last_name].filter(Boolean).join(' ').trim()
            || e.email || `Employee #${e.employee_id}`;
        return {
            value: String(e.employee_id),
            label: e.employee_type ? `${name} — ${e.employee_type}` : name,
        };
    });

    const handleSubmit = () => {
        if (!employeeId || employeeId === 'all') return toast.warn('Select an employee');
        const punchAtIso = fromLocalInputValue(punchAt);
        if (!punchAtIso) return toast.warn('Enter the punch date & time');
        if (new Date(punchAtIso) > new Date()) return toast.warn('Punch time cannot be in the future');
        if (reason.trim().length < 3) return toast.warn('Reason must be at least 3 characters');

        dispatch(createManualPunch({
            employee_id: Number(employeeId),
            punch_type: punchType,
            punch_at: punchAtIso,
            manual_reason: reason.trim(),
            notes: notes.trim() || undefined,
            onSuccess: (result) => {
                onClose();
                // The punch lands on punch_at's attendance day — hand the
                // recomputed rollup back so the grid refreshes the right row.
                onDone?.(result);
            },
        }));
    };

    const inputClass = 'tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500';

    return (
        <ModalShell
            isOpen={isOpen}
            title="Add missed punch"
            icon="mdi:clock-plus-outline"
            onClose={onClose}
            busy={manualPunchLoading}
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={manualPunchLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-rounded-md tw-bg-blue-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                    {manualPunchLoading ? <Icon icon="mdi:loading" className="tw-animate-spin" /> : <Icon icon="mdi:check" />}
                    Record punch
                </button>
            }
        >
            <div className="tw-flex tw-flex-col tw-gap-3">
                <div>
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Employee <span className="tw-text-red-500">*</span></label>
                    <SearchableDropdown
                        value={employeeId}
                        onChange={(v) => setEmployeeId(v === 'all' ? '' : v)}
                        options={employeeOptions}
                        loading={employeesDropdownLoading}
                        placeholder="Select employee"
                    />
                </div>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Direction <span className="tw-text-red-500">*</span></label>
                        <select value={punchType} onChange={(e) => setPunchType(e.target.value)} className={inputClass}>
                            <option value="IN">IN — start of shift</option>
                            <option value="OUT">OUT — end of shift</option>
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Punch time <span className="tw-text-red-500">*</span></label>
                        <input
                            type="datetime-local"
                            value={punchAt}
                            max={nowLocalInputValue()}
                            onChange={(e) => setPunchAt(e.target.value)}
                            className={inputClass}
                        />
                    </div>
                </div>
                <div>
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Reason <span className="tw-text-red-500">*</span></label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        maxLength={255}
                        placeholder="e.g. Forgot to punch out at shift end (min 3 characters)"
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Notes</label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        maxLength={1000}
                        placeholder="Optional"
                        className={inputClass}
                    />
                </div>
                <p className="tw-text-[11px] tw-text-gray-400 tw-m-0">
                    The punch is recorded on the date of the punch time (not today), marked as
                    manual, and approved immediately. It carries no location.
                </p>
            </div>
        </ModalShell>
    );
};

export default ManualPunchModal;
